import React, { useEffect, useId, useRef, useState } from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';
import type { ConversationDetail } from '@ctd/agent-contract';
import { useConversationHistory } from '../hooks/use-conversation-history';
import type { ConversationHistory } from '../hooks/use-conversation-history';
import style from '../style.module.scss';

const RECENT_COUNT = 3;
const LOAD_MORE_WITHIN_PX = 48;

interface HistoryMenuProps {
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
    currentConversationId: string | null;
    onSelectConversation: (detail: ConversationDetail) => void;
    onNewConversation: () => void;
}

interface PopoverProps {
    history: ConversationHistory;
    currentConversationId: string | null;
    onSelectConversation: (detail: ConversationDetail) => void;
    onNewConversation: () => void;
    onClose: () => void;
}

/** Unmounted when the menu closes, so it always reopens compact and drops a load still pending. */
const HistoryPopover: React.FC<PopoverProps> = ({
    history,
    currentConversationId,
    onSelectConversation,
    onNewConversation,
    onClose,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [openFailed, setOpenFailed] = useState(false);
    const latestChoice = useRef(0);
    const titleId = useId();

    useEffect(
        () => () => {
            latestChoice.current += 1;
        },
        []
    );

    const choose = async (id: string) => {
        latestChoice.current += 1;
        const mine = latestChoice.current;
        setOpenFailed(false);
        const detail = await history.open(id);
        if (mine !== latestChoice.current) {
            return;
        }
        if (!detail) {
            setOpenFailed(true);
            return;
        }
        onSelectConversation(detail);
        onClose();
    };

    const onScroll = (event: React.UIEvent<HTMLUListElement>) => {
        const list = event.currentTarget;
        if (
            list.scrollHeight - list.scrollTop - list.clientHeight <
            LOAD_MORE_WITHIN_PX
        ) {
            history.loadMore();
        }
    };

    const shown = isExpanded
        ? history.conversations
        : history.conversations.slice(0, RECENT_COUNT);

    const items = shown.map(({ id, title }) => (
        <li key={id}>
            <button
                type="button"
                className={style.history_item}
                title={title}
                aria-current={id === currentConversationId ? 'true' : undefined}
                onClick={() => choose(id)}
            >
                {title}
            </button>
        </li>
    ));

    return (
        <div
            className={`${style.history_menu} ${
                isExpanded ? style.history_menu_expanded : ''
            }`}
            role="group"
            aria-label="Conversations"
        >
            <button
                type="button"
                className={style.history_item}
                onClick={() => {
                    onNewConversation();
                    onClose();
                }}
            >
                New conversation
            </button>
            {isExpanded ? (
                <>
                    <div className={style.history_sep} />
                    <h3 id={titleId} className={style.history_title}>
                        All conversations
                    </h3>
                    <ul
                        className={style.history_list_scroll}
                        aria-labelledby={titleId}
                        onScroll={onScroll}
                    >
                        {items}
                    </ul>
                    {history.isLoadingMore && (
                        <div
                            className={style.history_loading}
                            role="status"
                            aria-label="Loading"
                        >
                            <span className={style.pulse} aria-hidden="true" />
                        </div>
                    )}
                </>
            ) : (
                shown.length > 0 && (
                    <>
                        <div className={style.history_sep} />
                        <ul className={style.history_list}>{items}</ul>
                    </>
                )
            )}
            {!isExpanded && history.total > 0 && (
                <>
                    <div className={style.history_sep} />
                    <button
                        type="button"
                        className={`${style.history_item} ${style.history_all}`}
                        onClick={() => setIsExpanded(true)}
                    >
                        View all ({history.total})
                    </button>
                </>
            )}
            {history.hasError && (
                <p className={style.history_error} role="alert">
                    Couldn&apos;t load conversations
                </p>
            )}
            {openFailed && (
                <p className={style.history_error} role="alert">
                    Couldn&apos;t open this conversation
                </p>
            )}
        </div>
    );
};

/** The quiet clock beside the close button: recent conversations, the full list, a fresh start. */
export const HistoryMenu: React.FC<HistoryMenuProps> = ({
    isOpen,
    onOpenChange,
    currentConversationId,
    onSelectConversation,
    onNewConversation,
}) => {
    const history = useConversationHistory();
    const { refresh } = history;
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            refresh();
        }
    }, [isOpen, refresh]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const onMouseDown = (event: MouseEvent) => {
            if (!wrapperRef.current?.contains(event.target as Node)) {
                onOpenChange(false);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [isOpen, onOpenChange]);

    return (
        <div className={style.history} ref={wrapperRef}>
            <button
                type="button"
                className={style.history_trigger}
                aria-label="Conversation history"
                aria-haspopup="true"
                aria-expanded={isOpen}
                onClick={() => onOpenChange(!isOpen)}
            >
                <ClockCircleOutlined />
            </button>
            {isOpen && (
                <HistoryPopover
                    history={history}
                    currentConversationId={currentConversationId}
                    onSelectConversation={onSelectConversation}
                    onNewConversation={onNewConversation}
                    onClose={() => onOpenChange(false)}
                />
            )}
        </div>
    );
};
